/**
 * 숫자 표기 공용 헬퍼 — 화면마다 따로 있던 fmt/fmtNum/fmtM3/won 12벌을 한 곳으로 (2026-09-04).
 *
 * 규칙
 *   · 로케일은 항상 ko-KR (천 단위 콤마).
 *   · 단위(㎥·원·%)는 붙이지 않는다 — 호출부가 문맥에 맞게 붙인다.
 *   · null/NaN 처리는 헬퍼마다 다르므로 이름으로 구분한다 (fmtNum 은 0, fmtOrDash 는 "—").
 */

const LOCALE = "ko-KR";

/** 표시 전용: ISO 달력일을 YYYY/MM/DD로 변환. 저장·조회·정렬에는 원본을 사용한다. */
export function formatDisplayDate(value: string | null | undefined): string {
  return (value ?? "").replace(/^(\d{4})-(\d{2})-(\d{2})(?=$|[T\s])/, "$1/$2/$3");
}

/** 소수 최대 digits 자리, 그 이하는 버림 표기 없이 반올림. null/NaN → "0" */
export function fmtNum(n: number | null | undefined, digits = 0): string {
  return (Number(n) || 0).toLocaleString(LOCALE, { maximumFractionDigits: digits });
}

/** 정수 반올림 표기 (Math.round 후 콤마) — 금액·건수 */
export function fmtInt(n: number | null | undefined): string {
  return Math.round(Number(n) || 0).toLocaleString(LOCALE);
}

/** 원화 금액 — fmtInt 별칭 (호출부에서 "원" 을 붙인다) */
export const fmtWon = fmtInt;

/** 물량(㎥) — 소수 1자리까지 */
export function fmtM3(n: number | null | undefined, digits = 1): string {
  return fmtNum(n, digits);
}

/** 소수 digits 자리 고정 (1.0 / 1.50) — 배합표처럼 자릿수를 맞춰야 할 때 */
export function fmtFixed(n: number | null | undefined, digits: number): string {
  return Number.isFinite(Number(n)) && n != null ? Number(n).toFixed(digits) : "-";
}

/** 값 없음/0/무한 → "—", 아니면 fmtNum */
export function fmtOrDash(n: number | null | undefined, digits = 0, dash = "—"): string {
  if (n == null || !Number.isFinite(n) || n === 0) return dash;
  return fmtNum(n, digits);
}

/** 증감률 "+8.2%" / "-3.0%". null → "—" */
export function fmtPctDelta(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;
}

/** 비율(0~100) "12.3%" */
export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(digits)}%`;
}

/** 약식 금액 — 1.9억 / 4,260만 / 8,530 */
export function fmtWonShort(n: number): string {
  const a = Math.abs(n);
  if (a >= 100_000_000) return `${(n / 100_000_000).toFixed(1)}억`;
  if (a >= 10_000) return `${Math.round(n / 10_000).toLocaleString(LOCALE)}만`;
  return n.toLocaleString(LOCALE);
}

/** 지난 시각 → "방금 전" / "3분 전" / "2시간 전". 자주 갱신되는 자료의 갱신 표시용(하루를 넘겨도 시간 단위) */
export function fmtAgo(d: Date, now: number): string {
  const sec = Math.max(0, Math.floor((now - d.getTime()) / 1000));
  if (sec < 60) return "방금 전";
  if (sec < 3600) return `${Math.floor(sec / 60)}분 전`;
  return `${Math.floor(sec / 3600)}시간 전`;
}
